// ============================================================================
// Beacon — deal enrichment ("paste an address → get a filled deal file").
//
// One entry point, `enrichDeal(deal)`, that gathers everything Beacon can learn
// about a property and returns a SUGGESTED patch the user reviews before applying.
//
// Two runtimes, same contract:
//   • CLOUD  -> calls the Supabase Edge Function `enrich` (holds tokens/keys,
//               queries the heavy FL parcel roll, caches into `data_cache`).
//   • LOCAL  -> does what the browser can without secrets: geocode (keyless) and
//               Census ACS (if VITE_CENSUS_KEY is set). Parcel/FMR need the edge
//               function, so they're simply reported as unavailable locally.
//
// `buildDealPatch` is pure and unit-tested: it maps normalized source data onto
// Deal fields and records provenance for every change so the UI can show
// "taxes ← $X (FL DOR just value × est. millage)" before anything is written.
// ============================================================================

import type { Deal, FloodZone, OwnershipIntel, RentIntel } from "../types";
import { resolveTract } from "./eligibility";
import { logUsage, type Provider } from "./usage";
import { fetchAreaIntel, type AreaIntel, type ParcelIntel } from "./providers";
import { money0 } from "./format";
import { isCloud, getSupabase } from "./supabase";

/** Typical FL total millage (~20 mills). Used to estimate the POST-SALE tax bill
 *  from the appraiser's just value — the FL reassessment gotcha. Overridable. */
export const DEFAULT_FL_MILLAGE = 0.02;

export interface EnrichSource {
  source: string;
  ok: boolean;
  note?: string;
  pulledAt?: string;
}

/** Normalized inputs the enrichment can draw on (any subset may be present). */
export interface EnrichInputs {
  tract?: string;
  matchedAddress?: string;
  parcel?: ParcelIntel | null;
  area?: AreaIntel | null;
  ownership?: OwnershipIntel | null;
  rent?: RentIntel | null;
  fmrSafmr2?: number | null;
  flood?: FloodZone | null;
  millage?: number;
}

export interface FieldChange {
  field: keyof Deal;
  label: string;
  from: unknown;
  to: unknown;
  source: string;
  estimate?: boolean;
}

export interface DealEnrichment {
  matchedAddress?: string;
  tract?: string;
  sources: EnrichSource[];
  changes: FieldChange[];
  patch: Partial<Deal>;
  notes: string[]; // insights that inform judgment but don't map to a single field
}

/** Comps-strength (1–3) from the number of comparable listings behind an estimate. */
export function deriveCompsStrength(compCount: number): 1 | 2 | 3 {
  if (compCount >= 8) return 3;
  if (compCount >= 4) return 2;
  return 1;
}

const num = (v: unknown): v is number => typeof v === "number" && isFinite(v) && v > 0;

/**
 * Pure: turn normalized source data into a reviewable set of Deal changes.
 * Only proposes a field when the source gives something trustworthy, and never
 * overwrites the user's negotiated purchase price (surfaced as context instead).
 */
export function buildDealPatch(deal: Deal, i: EnrichInputs): { changes: FieldChange[]; patch: Partial<Deal>; notes: string[] } {
  const changes: FieldChange[] = [];
  const notes: string[] = [];
  const add = (field: keyof Deal, label: string, to: unknown, source: string, estimate = false) => {
    if (to == null || to === "" || (deal as unknown as Record<string, unknown>)[field as string] === to) return;
    changes.push({ field, label, from: (deal as unknown as Record<string, unknown>)[field as string], to, source, estimate });
  };

  if (i.tract) add("censusTract", "Census tract", i.tract, "US Census Geocoder");

  if (i.parcel) {
    const p = i.parcel;
    if (num(p.yearBuilt) && p.yearBuilt > 1800) add("yearBuilt", "Year built", p.yearBuilt, "FL DOR parcel");
    // FL reassessment: post-sale taxes track just value, not the seller's capped bill.
    const mills = i.millage ?? DEFAULT_FL_MILLAGE;
    if (num(p.justValue)) add("taxes", "Property taxes (est.)", Math.round(p.justValue * mills), `FL DOR just value × ${(mills * 100).toFixed(1)} mills`, true);
  }

  if (i.area && i.area.renterSharePct != null) {
    add("renterRising", "Renter-majority tract", i.area.renterSharePct >= 50, `Census ACS (${i.area.renterSharePct}% renters)`);
  }

  if (i.ownership) {
    // title & distress signals feed Gate D (a lien is a boolean-ish presence,
    // pre-foreclosure a motivated-seller + title flag)
    add("lisPendens", "Pre-foreclosure (lis pendens)", i.ownership.lisPendens, "ATTOM recorded data");
    add("openLiens", "Open liens / judgments", i.ownership.openLiens, "ATTOM recorded data");
    if (num(i.ownership.estimatedEquity)) {
      notes.push(`ATTOM: est. seller equity ${money0(i.ownership.estimatedEquity)}${i.ownership.avm ? ` (AVM ${money0(i.ownership.avm)})` : ""}.`);
    }
  }

  if (i.rent) {
    // real comps drive the comps-strength signal and the HUD rent-reasonableness read
    add("comps", "Comps strength", i.rent.compsStrength, `RentCast (${i.rent.compCount} comps)`);
    if (num(i.rent.marketRent2)) {
      const { rangeLow: lo, rangeHigh: hi } = i.rent;
      const verdict = hi && deal.u2r > hi
        ? `**above** the comp range — rent-reasonableness risk`
        : `within the comp range — supports rent reasonableness`;
      notes.push(
        `RentCast: 2BR market rent ≈ ${money0(i.rent.marketRent2)}` +
        `${lo && hi ? ` (range ${money0(lo)}–${money0(hi)})` : ""} across ${i.rent.compCount} comps; ` +
        `your contract ${money0(deal.u2r)} is ${verdict}.`
      );
    }
  }

  if (num(i.fmrSafmr2)) add("safmr2", "2BR SAFMR", i.fmrSafmr2, "HUD FMR");
  if (i.flood) add("flood", "Flood zone", i.flood, "FEMA NFHL");

  const patch: Partial<Deal> = {};
  for (const c of changes) (patch as Record<string, unknown>)[c.field as string] = c.to;
  return { changes, patch, notes };
}

// ---------------------------------------------------------------------------
// Orchestrators
// ---------------------------------------------------------------------------

/** CLOUD: call the Edge Function, which does the heavy/keyed work server-side.
 *  `fast` skips the slow parcel/paid sources — for bulk address scanning. */
async function enrichViaEdge(deal: Deal, fast = false): Promise<DealEnrichment | null> {
  const sb = getSupabase();
  if (!sb) return null;
  const { data, error } = await sb.functions.invoke("enrich", { body: { address: deal.addr, fast } });
  if (error || !data) return null;
  const i = data as EnrichInputs & { sources?: EnrichSource[]; matchedAddress?: string; tract?: string };
  const { changes, patch, notes } = buildDealPatch(deal, i);
  return { matchedAddress: i.matchedAddress, tract: i.tract, sources: i.sources ?? [], changes, patch, notes };
}

/** LOCAL: geocode (keyless) + ACS (if key). Parcel/FMR are edge-only. */
async function enrichLocally(deal: Deal): Promise<DealEnrichment> {
  const sources: EnrichSource[] = [];
  const inputs: EnrichInputs = {};

  try {
    const t = await resolveTract(deal.addr);
    if (t) {
      inputs.tract = t.geoid;
      inputs.matchedAddress = t.matchedAddress;
      sources.push({ source: "US Census Geocoder", ok: true });
    } else {
      sources.push({ source: "US Census Geocoder", ok: false, note: "No address match" });
    }
  } catch {
    sources.push({ source: "US Census Geocoder", ok: false, note: "Geocoder unreachable" });
  }

  if (inputs.tract) {
    try {
      const area = await fetchAreaIntel(inputs.tract);
      if (area) {
        inputs.area = area;
        sources.push({ source: area.source, ok: true });
      } else {
        sources.push({ source: "Census ACS", ok: false, note: "Add a free VITE_CENSUS_KEY to enable" });
      }
    } catch {
      sources.push({ source: "Census ACS", ok: false, note: "Add a free VITE_CENSUS_KEY to enable" });
    }
  }

  // Parcel assessments, HUD FMR, and ATTOM (mortgages/liens/distress) require
  // server-side secrets / the heavy roll — they resolve in the Edge Function.
  sources.push({ source: "FL DOR parcel (assessments)", ok: false, note: "Needs the cloud Edge Function" });
  sources.push({ source: "HUD FMR", ok: false, note: "Needs the cloud Edge Function" });
  sources.push({ source: "ATTOM (mortgages/liens/distress)", ok: false, note: "Needs cloud + a paid ATTOM key" });
  sources.push({ source: "RentCast (rent comps)", ok: false, note: "Needs cloud + a paid RentCast key" });

  const { changes, patch, notes } = buildDealPatch(deal, inputs);
  return { matchedAddress: inputs.matchedAddress, tract: inputs.tract, sources, changes, patch, notes };
}

function providerFor(source: string): Provider | null {
  const s = source.toLowerCase();
  if (s.includes("attom")) return "ATTOM";
  if (s.includes("rentcast")) return "RentCast";
  if (s.includes("regrid")) return "Regrid";
  if (s.includes("hud") || s.includes("fmr")) return "HUD FMR";
  if (s.includes("census") || s.includes("acs") || s.includes("geocoder")) return "Census";
  if (s.includes("fema") || s.includes("flood")) return "FEMA";
  return null;
}

/** Public entry point. Uses the Edge Function in cloud mode, browser feeds locally. */
export async function enrichDeal(deal: Deal, opts?: { fast?: boolean }): Promise<DealEnrichment> {
  let r: DealEnrichment;
  if (isCloud()) r = (await enrichViaEdge(deal, opts?.fast)) ?? (await enrichLocally(deal));
  else r = await enrichLocally(deal);
  for (const s of r.sources) if (s.ok) { const p = providerFor(s.source); if (p) void logUsage(p, "enrich"); }
  return r;
}
